import { useState, useEffect, useRef, useMemo, useCallback, type MouseEvent as ReactMouseEvent } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../../contexts/SnackbarProvider';
import { SnackbarType } from '../Snackbar';
import { useDownloadProgress } from '../../contexts/DownloadProgressProvider';
import { SessionManager } from '../../utils/SessionManager';
import { favoriteVideo, unfavoriteVideo, checkFavoriteStatus } from '../../services/favoriteApi';
import { deleteProcessingVideo, deleteVideo } from '../../services/videoApi';
import { isInternalNetworkOnlyError } from '../../utils/internalNetworkError';
import { PROCESSING_DELETE_INTERNAL_ONLY } from '../../constants/accessMessages';
import axios from 'axios';
import { getServerErrorMessage } from '../../utils/apiErrorMessage';
import { useKebabMenu, UseKebabMenuReturn } from '../common/KebabMenu';
import { UI } from '../../config/constants';
import { downloadVideo } from '../../utils/videoDownload';
import { 
  VideoCardMobileProps, 
  DialogConfig,
  UseVideoCardMobileReturn,
  VideoImage
} from './types';
import {
  formatDate,
  formatDuration,
  formatViewCount,
  getElapsedTime
} from './utils';
import { toRelativePath } from '../../utils/videoImagePath';

/**
 * VideoCardMobileコンポーネントのためのカスタムフック
 * 
 * @param props VideoCardMobileのプロパティ
 * @returns ビジネスロジックと状態
 */
export const useVideoCardMobile = (props: VideoCardMobileProps): UseVideoCardMobileReturn => {
  const { video, hideVisibilityToggle = false, onVisibilityChange } = props;
  
  const showSnackbar = useSnackbar();
  const { startDownloadProgress, updateDownloadProgress, completeDownloadProgress } = useDownloadProgress();
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);
  // 動画の可視性状態を内部ステートとして管理
  const [visibility, setVisibility] = useState<'public' | 'private' | 'hospital_only'>(video.visibility || 'private');
  // Swiper関連の状態管理
  const [hasBeenSwiped, setHasBeenSwiped] = useState(false);
  const [isAutoSwipe, setIsAutoSwipe] = useState(true);
  const swiperRef = useRef<SwiperType | null>(null);
  
  // ダイアログの状態管理
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState<DialogConfig>({
    title: '',
    message: '',
    confirmText: '確認',
    type: 'info',
    onConfirm: () => {},
  });


  // SessionManager からアクティブセッションを取得
  const activeSession = SessionManager.getActiveSession();
  const activeUserId = activeSession ? activeSession.userId : null;
  const token = activeSession ? activeSession.token : null;

  // 投稿者アイコン（先頭スラッシュ正規化: icon_path の有無・スラッシュ有無を吸収）
  const uploaderIconUrl = useMemo(() => {
    return video.uploader?.icon_path
      ? toRelativePath(video.uploader.icon_path)
      : '/asset/no_icon_user.png';
  }, [video.uploader?.icon_path]);
  
  // 病院名 - ユーザー名
  const hospitalName = useMemo(() => {
    return video.uploader?.hospital?.name || '所属なし';
  }, [video.uploader?.hospital?.name]);
  
  const uploaderName = useMemo(() => {
    return video.uploader?.name || '不明なユーザー';
  }, [video.uploader?.name]);
  
  const channelLabel = useMemo(() => {
    return `${hospitalName} - ${uploaderName}`;
  }, [hospitalName, uploaderName]);
  
  // 投稿者かどうかの判定
  const isOwner = useMemo(() => {
    return !!activeUserId && video.uploader_id === activeUserId;
  }, [activeUserId, video.uploader_id]);
  
  // 画像配列を取得（カルーセル表示用）
  const images: VideoImage[] = useMemo(() => {
    return video.images || [];
  }, [video.images]);
  
  const hasMultipleImages = useMemo(() => {
    return images && images.length > 1;
  }, [images]);

  // お気に入り状態を取得
  // 処理中の動画（session_プレフィックス付きID）はスキップ
  useEffect(() => {
    if (activeUserId && token && !video.is_processing) {
      checkFavoriteStatus(video.id.toString())
        .then(status => {
          setIsFavorite(status);
        })
        .catch(err => {
          console.error('お気に入り状態の取得に失敗:', err);
        });
    }
  }, [video.id, activeUserId, token, video.is_processing]);

  // ユーザーの手動スワイプを検出
  const handleSlideChange = (swiper: SwiperType): void => {
    // autoplayによるスライド変更は無視する
    if (!isAutoSwipe && !hasBeenSwiped) {
      setHasBeenSwiped(true);
    }
  };

  // タッチイベント検出
  const handleTouchStart = (): void => {
    setIsAutoSwipe(false);
    if (!hasBeenSwiped) {
      setHasBeenSwiped(true);
    }
  };

  // ユーザー操作によるナビゲーション (矢印ボタンなど) の検出
  const handleNavigation = (): void => {
    setIsAutoSwipe(false);
    if (!hasBeenSwiped) {
      setHasBeenSwiped(true);
    }
  };

  // スワイプモードの自動検出
  const handleAutoplayStart = (): void => {
    setIsAutoSwipe(true);
  };

  // コンテナタップ時の処理
  const handleContainerClick = (e: ReactMouseEvent): void => {
    // 編集ボタン、お気に入りボタン、または非公開表示ボタンをクリックした場合はナビゲーションしない
    if (
      e.target instanceof Element && (
        e.target.closest('.edit-button-wrapper') ||
        e.target.closest('.video-card-mobile-favorite-button') ||
        e.target.closest('.video-card-mobile-private-icon') ||
        e.target.closest('.video-card-mobile-public-icon')
      )
    ) {
      return;
    }
    
    navigate(`/videos/${video.id}`);
  };

  // お気に入りボタンの処理
  const toggleFavorite = async (e: ReactMouseEvent): Promise<void> => {
    e.stopPropagation();

    // 処理中の動画はお気に入り操作を許可しない
    if (video.is_processing) {
      showSnackbar('処理中の動画はお気に入りに登録できません', SnackbarType.ERROR);
      return;
    }

    if (!activeUserId) {
      showSnackbar('ログインが必要です', SnackbarType.ERROR);
      return;
    }
    
    try {
      if (isFavorite) {
        await unfavoriteVideo(video.id.toString());
        setIsFavorite(false);
        showSnackbar('お気に入りから削除しました', SnackbarType.SUCCESS);
      } else {
        await favoriteVideo(video.id.toString());
        setIsFavorite(true);
        showSnackbar('お気に入りに追加しました', SnackbarType.SUCCESS);
      }
    } catch (error) {
      console.error('お気に入り処理エラー:', error);
      showSnackbar('処理に失敗しました', SnackbarType.ERROR);
    }
  };

  // 公開状態を変更するダイアログを表示
  const showVisibilityChangeDialog = (e: ReactMouseEvent, newVisibility: 'public' | 'private' | 'hospital_only'): void => {
    e.stopPropagation(); // カードのクリックイベントを停止
    
    // 投稿者本人のみ変更可能
    if (video.uploader_id !== activeUserId) {
      showSnackbar('動画の公開状態は投稿者のみ変更できます', SnackbarType.ERROR);
      return;
    }

    if (newVisibility === 'public') {
      setDialogConfig({
        title: '動画を外部公開',
        message: 'この動画を外部公開に変更しますか？院外ユーザーからも閲覧可能になります。',
        confirmText: '外部公開にする',
        type: 'warning',
        onConfirm: () => changeVisibility('public'),
      });
    } else if (newVisibility === 'hospital_only') {
      setDialogConfig({
        title: '動画を院内公開',
        message: 'この動画を院内公開に変更しますか？同じ病院のユーザーのみ閲覧できます。',
        confirmText: '院内公開にする',
        type: 'info',
        onConfirm: () => changeVisibility('hospital_only'),
      });
    } else {
      setDialogConfig({
        title: '動画を非公開',
        message: 'この動画を非公開に変更しますか？',
        confirmText: '非公開にする',
        type: 'info',
        onConfirm: () => changeVisibility('private'),
      });
    }
    
    setDialogOpen(true);
  };

  // 公開状態を変更する処理
  const changeVisibility = async (newVisibility: 'public' | 'private' | 'hospital_only'): Promise<void> => {
    try {
      await axios.put(
        `/api/videos/${video.id}/visibility`,
        { visibility: newVisibility },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setVisibility(newVisibility);
      showSnackbar(
        newVisibility === 'public'
          ? '動画を外部公開しました'
          : newVisibility === 'hospital_only'
            ? '動画を院内公開しました'
            : '動画を非公開にしました',
        SnackbarType.SUCCESS
      );
      
      // 親コンポーネントに通知（コールバック関数が提供されている場合）
      if (typeof onVisibilityChange === 'function') {
        onVisibilityChange(video.id, newVisibility);
      }
    } catch (error) {
      console.error('公開状態の更新に失敗:', error);
      // 診療科未設定で公開しようとした場合など、サーバーが理由を返すときはそれを見せる
      showSnackbar(getServerErrorMessage(error) ?? '公開状態の更新に失敗しました', SnackbarType.ERROR);
    }
  };

  // 非公開動画かどうかを判定
  const isPrivate = visibility === 'private';

  // ダウンロードハンドラー
  const handleDownload = useCallback(async (e: ReactMouseEvent): Promise<void> => {
    e.stopPropagation();
    const activeSession = SessionManager.getActiveSession();
    const token = activeSession ? activeSession.token : null;
    const defaultFileName = video.title || video.name || 'video';
    
    await downloadVideo(
      video.id,
      token || '',
      defaultFileName,
      undefined,
      (errorMessage) => {
        showSnackbar(errorMessage, SnackbarType.ERROR);
      },
      {
        startDownloadProgress,
        updateDownloadProgress,
        completeDownloadProgress
      }
    );
  }, [video.id, video.title, video.name, showSnackbar, startDownloadProgress, updateDownloadProgress, completeDownloadProgress]);

  // 動画削除ハンドラー（処理中動画と通常動画の両方に対応）
  const handleDelete = useCallback(async (e: ReactMouseEvent): Promise<void> => {
    e.stopPropagation();

    // 処理中動画の判定: is_processingフラグまたはIDがsession_で始まる場合
    const videoIdStr = String(video.id);
    const isProcessing = video.is_processing || videoIdStr.startsWith('session_');

    // 削除確認ダイアログを表示
    setDialogConfig({
      title: isProcessing ? '変換中の動画を削除' : '動画を削除',
      message: isProcessing
        ? 'この動画の変換処理をキャンセルして削除しますか？この操作は取り消せません。'
        : 'この動画を削除しますか？この操作は取り消せません。',
      confirmText: '削除する',
      type: 'warning',
      onConfirm: async () => {
        try {
          if (isProcessing) {
            await deleteProcessingVideo(videoIdStr);
          } else {
            await deleteVideo(videoIdStr);
          }
          showSnackbar('動画を削除しました', SnackbarType.SUCCESS);
          // ページをリロードして一覧を更新
          window.location.reload();
        } catch (error) {
          console.error('動画の削除に失敗:', error);
          // 院外から処理中動画を削除しようとした場合は 403 INTERNAL_NETWORK_ONLY が返る。
          // 汎用文言で潰さず、院内限定である旨の専用文言を出す（download / upload と同じ扱い）。
          // isProcessing との AND を取るのは、この文言が処理中動画専用だから
          // （accessMessages.ts の PROCESSING_DELETE_INTERNAL_ONLY 参照）。
          // 将来ゲート適用範囲が通常削除に広がっても、誤って「変換中の…」と案内しない。
          showSnackbar(
            isProcessing && isInternalNetworkOnlyError(error)
              ? PROCESSING_DELETE_INTERNAL_ONLY
              : '動画の削除に失敗しました',
            SnackbarType.ERROR
          );
        }
      },
    });
    setDialogOpen(true);
  }, [video.id, video.is_processing, showSnackbar]);

  // ケバブメニューの状態管理（共通フックを使用）
  const kebabMenu: UseKebabMenuReturn = useKebabMenu({
    video,
    isFavorite,
    isPrivate,
    toggleFavorite,
    showVisibilityChangeDialog,
    navigate,
    leftOffset: UI.KEBAB_MENU_LEFT_OFFSET.SIDE_AND_MOBILE,
    onDownload: handleDownload,
    onDelete: handleDelete
  });

  return {
    // 状態
    isFavorite,
    visibility,
    dialogOpen,
    dialogConfig,
    hasBeenSwiped,
    isAutoSwipe,
    swiperRef,
    
    // 計算された値
    uploaderIconUrl,
    channelLabel,
    hospitalName,
    uploaderName,
    isOwner,
    images,
    hasMultipleImages,
    isPrivate,
    
    // ハンドラー
    handleSlideChange,
    handleTouchStart,
    handleNavigation,
    handleAutoplayStart,
    handleContainerClick,
    toggleFavorite,
    setDialogOpen,
    
    // ユーティリティ関数
    formatViewCount,
    formatDuration,
    formatDate,
    getElapsedTime,
    
    // ケバブメニュー関連（共通フックから取得）
    kebabMenu,
    showVisibilityChangeDialog
  };
}; 
